import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, callableErrorMessage } from "../lib/api";
import { rememberRedirect } from "../lib/redirectAfter";

export function AcceptInvite() {
  const { user, claims, refreshClaims, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tenantId = params.get("tenant");
  const token = params.get("token");
  const isPlatformInvite = params.get("platform") === "1";
  const [status, setStatus] = useState<"idle" | "working" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!token || (!isPlatformInvite && !tenantId)) {
      setError("Link inválido.");
      setStatus("error");
      return;
    }
    if (!user) {
      const dest = isPlatformInvite
        ? `/aceptar-invitacion?platform=1&token=${token}`
        : `/aceptar-invitacion?tenant=${tenantId}&token=${token}`;
      rememberRedirect(dest);
      nav("/login", { replace: true });
      return;
    }
    if (user.isAnonymous) {
      setError("Inicia sesión con la cuenta del email que recibió la invitación.");
      setStatus("error");
      return;
    }
    if (!isPlatformInvite && claims.tenantId === tenantId && (claims.role === "tenant_admin" || claims.role === "ambassador")) {
      setStatus("ok");
      setTimeout(() => nav("/", { replace: true }), 600);
      return;
    }
    if (isPlatformInvite && claims.role === "platform_admin") {
      setStatus("ok");
      setTimeout(() => nav("/platform", { replace: true }), 600);
      return;
    }
    let cancelled = false;
    (async () => {
      setStatus("working");
      try {
        if (isPlatformInvite) {
          await api.acceptPlatformInvite({ token: token! });
        } else {
          await api.acceptInvite({ tenantId: tenantId!, token: token! });
        }
        await refreshClaims();
        if (!cancelled) {
          setStatus("ok");
          setTimeout(() => nav(isPlatformInvite ? "/platform" : "/", { replace: true }), 800);
        }
      } catch (err) {
        if (!cancelled) {
          setError(callableErrorMessage(err));
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user, tenantId, token, isPlatformInvite]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col items-center gap-6 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza</p>

        {(status === "idle" || status === "working") && (
          <>
            <h1 className="font-display text-2xl font-semibold text-leaf-950">
              Aceptando invitación…
            </h1>
            <Spinner />
          </>
        )}

        {status === "ok" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-leaf-950">
              ¡Listo! Ya tienes acceso
            </h1>
            <p className="text-sm text-leaf-700">Redirigiendo a tu panel…</p>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-leaf-950">
              No pudimos aceptar la invitación
            </h1>
            <p className="text-sm text-leaf-700">{error}</p>
            <button onClick={() => nav("/login")} className="btn-primary">
              Ir al login
            </button>
          </>
        )}
      </div>
    </main>
  );
}

function Spinner() {
  return (
    <svg className="h-6 w-6 animate-spin text-leaf-600" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
